/**
 * POS-I118 — PKR / USD display of the public price list.
 * Same rule as the backend (backend/src/modules/subscription/pricing-usd.ts): 278 PKR = 1 USD,
 * whole dollars from 10 up, nearest 0.25 below 10. Billing stays PKR; this is display only.
 */
export type DisplayCurrency = 'PKR' | 'USD';

export function usdFromPkr(pkr: number): number {
  if (!pkr || pkr <= 0) return 0;
  const raw = pkr / 278;
  if (raw >= 10) return Math.round(raw);
  return Math.round(raw * 4) / 4;
}

export function formatUsd(usd: number): string {
  return Number.isInteger(usd) ? `$${usd}` : `$${usd.toFixed(2)}`;
}

/** Splits a list price such as "1,000/promo" into its numeric PKR part and unit suffix. */
export function parseListPrice(price: string): { pkr: number; suffix: string } {
  const m = price.match(/^([\d,]+(?:\.\d+)?)(.*)$/);
  if (!m) return { pkr: 0, suffix: '' };
  return { pkr: Number(m[1].replace(/,/g, '')), suffix: m[2] };
}

/**
 * Text for one list price in the chosen currency. `storedUsd` is the stored USD price from the public
 * catalogue (pricingByCurrency.USD); when it is missing the derived value is used.
 */
export function displayListPrice(price: string, currency: DisplayCurrency, storedUsd?: number | null): string {
  const { pkr, suffix } = parseListPrice(price);
  if (currency === 'PKR') return price;
  return `${formatUsd(storedUsd ?? usdFromPkr(pkr))}${suffix}`;
}

export function displayBasePrice(pkr: number, currency: DisplayCurrency, storedUsd?: number | null): string {
  if (currency === 'PKR') return `PKR ${pkr.toLocaleString('en-US')}`;
  return `USD ${formatUsd(storedUsd ?? usdFromPkr(pkr))}`;
}
